import { createRouter, createWebHistory } from 'vue-router';
import pinia from "./store";
import { useSettingsStore } from '@store/settings';

// Default Pages
import Index from '@pages/index.vue';
import Payment from '@pages/payment.vue';
import PaymentCheckout from '@pages/payment-checkout.vue';
import Help from '@pages/help.vue';
import Invite from '@pages/invite.vue';
import Instruction from '@pages/instruction.vue';

// Account
import AccountIndex from '@pages/account/index.vue';
import AccountEmail from '@pages/account/email.vue';
import AccountEmailVerified from '@pages/account/email-verified.vue';
import AccountHistoryPayment from '@pages/account/historyPayment.vue';
import AccountLanguage from '@pages/account/language.vue';
import AccountSubscribe from '@pages/account/subscribe.vue';
import AccountReserve from '@pages/account/reserve.vue';

// Settings
import SettingsProtocol from '@/pages/settings/protocol.vue';
import SettingsOs from '@pages/settings/os.vue';
import SettingsCountry from '@pages/settings/country.vue';
import SettingsKey from '@pages/settings/key.vue';

//VPN
import RunVpn from "@pages/runVpn.vue";
import Instructions from "@pages/settings/instructions.vue";

// Authorize
import Login from "@pages/login.vue";
import Welcome from "@pages/welcome.vue";
import Connect from '@pages/connect.vue';
import { useAuthApi } from '@store/api/authApi';
import { usePaymentApi } from '@store/api/paymentApi';
import { usePaymentFlowStore } from '@store/paymentFlow';
import { useToast } from 'vue-toast-notification';
import i18n from './i18n';
import isInTelegram from './utils/isInTelegram';
import { gtmPageView } from '@/composables/gtm';
import { awaitPendingHandoff } from '@composables/handoff';

const settingsStore = useSettingsStore(pinia);
const { getFirstRouteName } = settingsStore;

const routes = [
	{
		name: "Home",
		path: '/',
		component: Index
	},
	{
		name: "Payment",
		path: '/payment',
		component: Payment
	},
	{
		name: "PaymentCheckout",
		path: '/payment/checkout',
		component: PaymentCheckout
	},
	{
		name: "Help",
		path: '/help',
		component: Help
	},
	{
		name: "Invite",
		path: '/invite',
		component: Invite
	},
	{
		name: "Account",
		path: '/account',
		component: AccountIndex
	},
	{
		name: "AccountEmail",
		path: '/account/email',
		component: AccountEmail
	},
	{
		name: "AccountEmailVerified",
		path: '/account/email/verified',
		component: AccountEmailVerified
	},
	{
		name: "AccountHistory",
		path: '/account/history',
		component: AccountHistoryPayment
	},
	{
		name: "AccountLanguage",
		path: '/account/language',
		component: AccountLanguage
	},
	{
		name: "AccountSubscribe",
		path: '/account/subscribe',
		component: AccountSubscribe
	},
	{
		// Не в publicRoutes: состояние резерва читается по токену пользователя
		name: "AccountReserve",
		path: '/account/reserve',
		component: AccountReserve
	},
	{
		name: "Settings",
		path: '/settings/:groupName?',
		redirect: (to: any) => {
			const { groupName } = to.params;
			const routeName = getFirstRouteName(groupName);
			return {
				name: routeName || "Home",
				params: { groupName }
			};
		},
	},
	{
		name: 'SettingsOs',
		path: '/settings/:groupName/os',
		component: SettingsOs,
	},
	{
		name: 'SettingsCountry',
		path: '/settings/:groupName/country',
		component: SettingsCountry,
	},
	{
		name: 'SettingsKey',
		path: '/settings/:groupName/key',
		component: SettingsKey,
	},
	{
		name: 'SettingsProtocol',
		path: '/settings/:groupName/protocol',
		component: SettingsProtocol,
	},
	{
		name: 'DownloadApp',
		path: '/settings/:groupName/app',
		component: Instructions,
	},
	{
		name: 'Instructions',
		path: '/settings/:groupName/instructions',
		component: Instructions,
	},
	{
		name: 'RunVpn',
		path: '/vpn/run/:app',
		component: RunVpn,
	},
	{
		name: 'Welcome',
		path: '/welcome/:callback?',
		component: Welcome,
	},
	{
		name: 'Login',
		path: '/login/:callback?',
		component: Login,
	},
	{
		name: 'Connect',
		path: '/connect/:callback',
		component: Connect,
	},
	{
		name: 'Instruction',
		path: '/instruction',
		component: Instruction,
	},
];

const router = createRouter({
	history: createWebHistory(),
	routes
});

router.beforeEach(async (to) => {
	// Wait for an in-flight cross-domain handoff exchange to establish the
	// session, so the redirect/promo logic below sees the authed user on the
	// very first navigation (e.g. landing on /welcome?redirect=payment#handoff=).
	await awaitPendingHandoff();

	const authApi = useAuthApi();
	const token = authApi.methods.getToken();
	const areAuthRoutes = to.name === 'Login' || to.name === 'Welcome';
	const isMiniApp = isInTelegram();

	// Payment pages are accessible without auth
	const publicRoutes = ['Connect', 'AccountEmailVerified', 'Payment', 'PaymentCheckout', 'Welcome'];
	if (!isMiniApp && !token && !areAuthRoutes && !publicRoutes.includes(to.name as string)) {
		return { name: 'Login' };
	}

	if (!isMiniApp && token && areAuthRoutes) {
		const redirect = Array.isArray(to.query.redirect) ? to.query.redirect[0] : to.query.redirect as string;
		const promo = Array.isArray(to.query.promo) ? to.query.promo[0] : to.query.promo as string;
		const query = { ...to.query };
		delete query.redirect;
		delete query.email;

		if (promo) {
			const paymentFlow = usePaymentFlowStore();
			const paymentApi = usePaymentApi();
			const toast = useToast();
			paymentFlow.promoCode = promo;
			paymentApi.http.applyPromoCode(promo)
				.then(() => {
					toast.success(i18n.global.t('payment.promo_applied'), { position: 'top' });
				})
				.catch(e => {
					console.error('Failed to apply promo from welcome redirect', e);
					// A rejected code must not linger in the store and ride into the
					// checkout; the payment page re-checks the one in the URL itself
					if (paymentApi.methods.promoRejectionCode(e) && paymentFlow.promoCode === promo) {
						paymentFlow.clearPromo();
					}
				});
		}

		if (redirect === 'payment') {
			return { name: 'Payment', query };
		}
		return { name: 'Home', query };
	}
})

router.afterEach((to) => {
	gtmPageView(to.fullPath);
});

export default router;
